import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ROLE_BADGE = { affiliate: "REP", manager: "MANAGER", admin: "ADMIN" };

// Build 13 fix (Sep 19 2026) — used to only ever return managers and never actually navigate
// anywhere on click. Now returns every rep/manager/admin (scoped to a manager's own downline
// when a manager is the one searching) and jumps straight to the right row.
export function RepSearchDropdown() {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    api.get("/admin/reps/search-list")
      .then(({ data }) => setPeople(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load reps"));
  }, [open]);

  const jumpToPerson = (person) => {
    setOpen(false);
    if (user?.role === "manager") {
      navigate(`/manager?tab=downline&focus=${person.id}`);
    } else {
      navigate(`/admin/users?focus=${person.id}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="rep-search-dropdown-trigger"
          title="Search for any rep, manager, or admin by name, code, or email and jump straight to their row — quicker than scrolling the full roster."
          className="flex items-center gap-2 text-xs text-igc-muted hover:text-igc-goldText border border-igc-gold/20 px-3 py-2 transition-colors"
        >
          <MagnifyingGlass size={14} /> Jump to Rep
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80 bg-igc-surface border-igc-gold/20" data-testid="rep-search-dropdown-content">
        <Command className="bg-igc-surface text-igc-cream">
          <CommandInput data-testid="rep-search-dropdown-input" placeholder="Search by name, code, or email..." title="Type a name, rep code, or email to filter this list — results appear as you type." className="text-igc-cream" />
          <CommandList>
            <CommandEmpty className="text-igc-muted text-xs py-4 text-center">No matches found.</CommandEmpty>
            <CommandGroup>
              {people.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.code} ${p.display_name} ${p.email || ""}`}
                  onSelect={() => jumpToPerson(p)}
                  data-testid={`rep-search-result-${p.id}`}
                  className="text-igc-cream data-[selected=true]:bg-igc-gold/15 data-[selected=true]:text-igc-goldText cursor-pointer flex items-center gap-2"
                >
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-igc-gold/15 text-igc-goldText shrink-0" data-testid={`rep-search-result-role-${p.id}`}>{ROLE_BADGE[p.role] || p.role}</span>
                  <span className="text-igc-goldText mr-1">{p.code}</span> {p.display_name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
